import type {
  CreateLayGroupResponse,
  UpdateLayGroupsResponse,
  LoadLayGroupsResponse,
  PartialGroup,
} from './types';
import type { Group } from '@/shared/types/group';
import { apiInvoke, shouldLoad } from '../helpers/load';
import { makePayloadRequestInit } from '@/lib/api';
import { Paths } from '../constants';
import type { APILoadParams, APIParams } from '@/store/helpers/types';
import type { GroupsStore } from '.';

export async function loadGroups(
  { loaded: loadedRefs, state, dispatch }: GroupsStore,
  {
    force,
    api,
    auth,
    campus,
    campuses,
  }: {
    campus?: string;
    campuses?: string[];
  } & APILoadParams,
) {
  let loaded = state.allLoaded || loadedRefs.all.current;
  if (!loaded && campuses && !force) {
    campuses = campuses.filter(
      (item) =>
        !state.campusLoaded.has(item) && !loadedRefs.campus.current.has(item),
    );
    loaded = !campuses.length;
  }

  if (!loaded && campus && !force) {
    loaded =
      state.campusLoaded.has(campus) || loadedRefs.campus.current.has(campus);
  }

  if (!shouldLoad(loaded, force, auth)) {
    return;
  }

  if (campus) {
    loadedRefs.campus.current.add(campus);
  }

  if (campuses?.length) {
    campuses.forEach((campus) => loadedRefs.campus.current.add(campus));
  }

  if (!campus && !campuses?.length) {
    loadedRefs.all.current = true;
  }

  let path = Paths.groups;
  if (campuses) {
    path = `${path}?campus=${encodeURIComponent(JSON.stringify(campuses))}`;
  } else if (campus) {
    path = `${path}/${campus}`;
  }

  const res = await apiInvoke<LoadLayGroupsResponse>(api, path, auth);
  dispatch({ type: 'setGroups', data: res });
}

export async function createGroup(
  { dispatch }: GroupsStore,
  { api, auth, item }: { item: Group } & APIParams,
) {
  const res = await apiInvoke<CreateLayGroupResponse>(
    api,
    Paths.groups,
    auth,
    makePayloadRequestInit(item.data, 'POST'),
  );
  if (res.created) {
    const item: Group = { id: res.id, data: res.data };
    dispatch({ type: 'addGroup', item });
  }
}

export async function updateGroup(
  { dispatch }: GroupsStore,
  { api, auth, item }: { item: PartialGroup } & APIParams,
) {
  const path = `${Paths.groups}/${item.id}`;
  const res = await apiInvoke<UpdateLayGroupsResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(item.data, 'PATCH'),
  );
  if (res.updated) {
    const item: Group = { id: res.id, data: res.data };
    dispatch({ type: 'updateGroup', item });
  }
}

export async function archiveGroup(
  store: GroupsStore,
  { api, auth, item }: { item: Group } & APIParams,
) {
  const updates: PartialGroup = {
    id: item.id,
    data: {
      archived: true,
    },
  };
  await updateGroup(store, { api, auth, item: updates });
}
